import { ProfileSection } from '@/components/settings/ProfileSection'
import { NotificationsSection } from '@/components/settings/NotificationsSection'
import { LeakSitesSection } from '@/components/settings/LeakSitesSection'
import { SupplyChainSection } from '@/components/settings/SupplyChainSection'
import { IntegrationsSection } from '@/components/settings/IntegrationsSection'
import { AppearanceSection } from '@/components/settings/AppearanceSection'
import { PrivacySection } from '@/components/settings/PrivacySection'
import { DataExportSection } from '@/components/settings/DataExportSection'
import { AccountDeletionSection } from '@/components/settings/AccountDeletionSection'
import { SignOutSection } from '@/components/settings/SignOutSection'
import { SavedFiltersSection } from '@/components/settings/SavedFiltersSection'
import { KeywordWatchesSection } from '@/components/settings/KeywordWatchesSection'
import { TrackedVendorsSection } from '@/components/settings/TrackedVendorsSection'
import { VendorPreferencesSection } from '@/components/vendor-content/VendorPreferencesSection'
import { vendorRoutingUiEnabled } from '@/components/vendor-content/routing-ui-flag'
import { RegulationWatchesPanel } from '@/components/settings/RegulationWatchesPanel'

/**
 * Settings — flat stacked sections.
 *
 * Design-playbook sub-project 3 wave 4 (T5.3): replaces the previous 7-tab
 * layout with a single scrollable column of labelled sections, matching the
 * playbook's Settings pattern. Each section renders in its own child component
 * under `components/settings/` so they can be reused or tested in isolation.
 */
export function SettingsPage() {
  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-10 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage your IntelHub preferences.
        </p>
      </header>

      <section aria-labelledby="settings-profile" className="flex flex-col gap-4">
        <h2 id="settings-profile" className="text-lg font-semibold">
          Profile
        </h2>
        <ProfileSection />
      </section>

      {/* `id="notifications"` is the deep-link target of the regulatory
          digest's unsubscribe page (KAN-836). */}
      <section
        aria-labelledby="settings-notifications"
        className="flex flex-col gap-4"
        id="notifications"
      >
        <h2 id="settings-notifications" className="text-lg font-semibold">
          Notifications
        </h2>
        <NotificationsSection />
      </section>

      {/* P5 Wave-1 settings primitive: saved filters + keyword/vendor inputs.
          Each list page seeds its filter state from these via
          `useFilterDefaultsFromPrefs(filterKey)`. */}
      <section
        aria-labelledby="settings-saved-filters"
        className="flex flex-col gap-4"
      >
        <h2 id="settings-saved-filters" className="text-lg font-semibold">
          Saved filters
        </h2>
        <SavedFiltersSection />
      </section>

      <section
        aria-labelledby="settings-keyword-watches"
        className="flex flex-col gap-4"
      >
        <h2 id="settings-keyword-watches" className="text-lg font-semibold">
          Keyword watches
        </h2>
        <KeywordWatchesSection />
      </section>

      <section
        aria-labelledby="settings-tracked-vendors"
        className="flex flex-col gap-4"
      >
        <h2 id="settings-tracked-vendors" className="text-lg font-semibold">
          Tracked vendors
        </h2>
        <TrackedVendorsSection />
      </section>

      {/* KAN-822 T9 — the saved Vendor updates preference (dark build flag). */}
      {vendorRoutingUiEnabled() && (
        <section aria-labelledby="settings-vendor-updates" className="flex flex-col gap-4">
          <h2 id="settings-vendor-updates" className="text-lg font-semibold">
            Vendor updates
          </h2>
          <VendorPreferencesSection />
        </section>
      )}

      <section
        aria-labelledby="settings-regulation-watches"
        className="flex flex-col gap-4"
        id="regulation-watches"
      >
        <h2 id="settings-regulation-watches" className="text-lg font-semibold">
          Regulation watches
        </h2>
        <RegulationWatchesPanel />
      </section>

      <section aria-labelledby="settings-leak-sites" className="flex flex-col gap-4">
        <h2 id="settings-leak-sites" className="text-lg font-semibold">
          Ransomware leak-site activity
        </h2>
        <LeakSitesSection />
      </section>

      <section aria-labelledby="settings-supply-chain" className="flex flex-col gap-4">
        <h2 id="settings-supply-chain" className="text-lg font-semibold">
          Supply chain &amp; cloud security
        </h2>
        <SupplyChainSection />
      </section>

      <section aria-labelledby="settings-integrations" className="flex flex-col gap-4">
        <h2 id="settings-integrations" className="text-lg font-semibold">
          Integrations
        </h2>
        <IntegrationsSection />
      </section>

      <section aria-labelledby="settings-appearance" className="flex flex-col gap-4">
        <h2 id="settings-appearance" className="text-lg font-semibold">
          Appearance
        </h2>
        <AppearanceSection />
      </section>

      <section aria-labelledby="settings-privacy" className="flex flex-col gap-4">
        <h2 id="settings-privacy" className="text-lg font-semibold">
          Privacy
        </h2>
        <PrivacySection />
      </section>

      <section aria-labelledby="settings-data-export" className="flex flex-col gap-4">
        <h2 id="settings-data-export" className="text-lg font-semibold">
          Data export
        </h2>
        <DataExportSection />
      </section>

      {/* KAN-144 — self-service account deletion (30-day reversible grace). */}
      <section
        aria-labelledby="settings-account-deletion"
        className="flex flex-col gap-4"
      >
        <h2 id="settings-account-deletion" className="text-lg font-semibold">
          Delete account
        </h2>
        <AccountDeletionSection />
      </section>

      <section aria-labelledby="settings-signout" className="flex flex-col gap-4">
        <h2 id="settings-signout" className="text-lg font-semibold">
          Sign out
        </h2>
        <SignOutSection />
      </section>
    </main>
  )
}
